import { useEffect, useState } from 'react';
import { Plus, RefreshCw, Search, X, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useDataSources,
  useDeleteDataSource,
  useDuplicateDataSource,
  useTriggerExtraction,
} from '@/hooks/use-data-sources';
import { SectionHeader } from '@/components/layout/SectionHeader';
import { DataSource, DataSourceType } from '@/types/data-source';
import DataSourcesTable from './components/DataSourcesTable';
import DataSourceForm from './components/DataSourceForm';
import DuplicateDataSourceDialog from './components/DuplicateDataSourceDialog';
import ExtractionHistory from './components/ExtractionHistory';
import { ExtractionPreviewDialog } from './components/ExtractionPreviewDialog';
import { toast } from 'sonner';

const DUPLICATE_DRAFT_STORAGE_KEY = 'dwp:data-source-duplicate-draft:v1';

interface DuplicateTarget {
  id: string;
  name: string;
}

export default function DataSourcesPage() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedDataSource, setSelectedDataSource] = useState<DataSource | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<DuplicateTarget | null>(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [historyDataSource, setHistoryDataSource] = useState<DataSource | null>(null);
  const [previewDataSource, setPreviewDataSource] = useState<DataSource | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | DataSourceType>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data, isLoading, refetch } = useDataSources({
    page,
    limit: pageSize,
    type: typeFilter === 'all' ? undefined : typeFilter,
    search: searchQuery || undefined,
  });
  const deleteMutation = useDeleteDataSource();
  const duplicateMutation = useDuplicateDataSource();
  const extractMutation = useTriggerExtraction();

  const dataSources = data?.dataSources || [];
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const currentPage = pagination?.page || page;

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [searchQuery, typeFilter, pageSize]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const rawDraft = window.sessionStorage.getItem(DUPLICATE_DRAFT_STORAGE_KEY);
    if (!rawDraft) return;

    try {
      const parsed = JSON.parse(rawDraft) as {
        sourceId?: string;
        sourceName?: string;
        duplicateName?: string;
      };

      if (!parsed.sourceId || !parsed.sourceName) {
        window.sessionStorage.removeItem(DUPLICATE_DRAFT_STORAGE_KEY);
        return;
      }

      setDuplicateTarget({
        id: parsed.sourceId,
        name: parsed.sourceName,
      });
      setDuplicateName(parsed.duplicateName || `${parsed.sourceName} (Copy)`);
      setIsDuplicateDialogOpen(true);
    } catch {
      window.sessionStorage.removeItem(DUPLICATE_DRAFT_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!isDuplicateDialogOpen || !duplicateTarget) {
      window.sessionStorage.removeItem(DUPLICATE_DRAFT_STORAGE_KEY);
      return;
    }

    window.sessionStorage.setItem(
      DUPLICATE_DRAFT_STORAGE_KEY,
      JSON.stringify({
        sourceId: duplicateTarget.id,
        sourceName: duplicateTarget.name,
        duplicateName,
      })
    );
  }, [isDuplicateDialogOpen, duplicateName, duplicateTarget]);

  const handleEdit = (dataSource: DataSource) => {
    setSelectedDataSource(dataSource);
    setIsFormOpen(true);
  };

  const handleCreate = () => {
    setSelectedDataSource(null);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedDataSource(null);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const closeDuplicateDialog = () => {
    setIsDuplicateDialogOpen(false);
    setDuplicateTarget(null);
    setDuplicateName('');
  };

  const handleDuplicate = (dataSource: DataSource) => {
    setDuplicateTarget({
      id: dataSource.id,
      name: dataSource.name,
    });
    setDuplicateName(`${dataSource.name} (Copy)`);
    setIsDuplicateDialogOpen(true);
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicateTarget) return;

    const name = duplicateName.trim();
    try {
      const duplicated = await duplicateMutation.mutateAsync({
        id: duplicateTarget.id,
        data: name ? { name } : undefined,
      });

      closeDuplicateDialog();
      setSelectedDataSource(duplicated);
      setIsFormOpen(true);
    } catch {
      // Error toast handled in mutation hook.
    }
  };

  const handleTest = async (id: string) => {
    // Test connection is handled through the form
    const source = dataSources.find((ds) => ds.id === id);
    if (source) {
      setSelectedDataSource(source);
      setIsFormOpen(true);
    }
  };

  const handleExtract = (id: string) => {
    const source = dataSources.find((ds) => ds.id === id);
    const query = source?.metadata?.defaultQuery as string | undefined;
    if (!query) {
      toast.error('No default query configured for this data source');
      return;
    }
    extractMutation.mutate({ dataSourceId: id, query });
  };

  const handleViewHistory = (dataSource: DataSource) => {
    setHistoryDataSource(dataSource);
  };

  const handlePreview = (dataSource: DataSource) => {
    setPreviewDataSource(dataSource);
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleSelectAll = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const ids = dataSources.map((source) => source.id);
      if (checked) {
        ids.forEach((id) => next.add(id));
      } else {
        ids.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkExtract = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const jobs = ids
      .map((id) => {
        const source = dataSources.find((ds) => ds.id === id);
        const query = source?.metadata?.defaultQuery as string | undefined;
        return query ? { dataSourceId: id, query } : null;
      })
      .filter(Boolean) as { dataSourceId: string; query: string }[];

    if (jobs.length === 0) {
      toast.error('No selected sources have a default query configured');
      return;
    }

    await Promise.allSettled(jobs.map((job) => extractMutation.mutateAsync(job)));
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} data source(s)? This cannot be undone.`)) {
      return;
    }
    await Promise.allSettled(ids.map((id) => deleteMutation.mutateAsync(id)));
    clearSelection();
  };

  const hasSelection = selectedIds.size > 0;

  return (
    <div className="space-y-6">
      <div data-testid="datasource-command-header">
        <SectionHeader
          title="Data Source Command Deck"
          subtitle="Manage ingestion endpoints, duplication flow, and extraction readiness from one place."
          metric={`${pagination?.total ?? dataSources.length} sources`}
          actions={(
            <>
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetch()}
                disabled={isLoading}
                aria-label="Refresh data sources"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Add Data Source
              </Button>
            </>
          )}
        />
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search data sources..."
              className="pl-9"
            />
          </div>
          <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value as any)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="DATABASE">Database</SelectItem>
              <SelectItem value="REST">REST</SelectItem>
              <SelectItem value="GRAPHQL">GraphQL</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Per page" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 / page</SelectItem>
              <SelectItem value="20">20 / page</SelectItem>
              <SelectItem value="50">50 / page</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-muted-foreground">
          {pagination?.total ?? dataSources.length} result
          {(pagination?.total ?? dataSources.length) === 1 ? '' : 's'}
        </div>
      </div>

      {hasSelection && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
          <Badge variant="secondary">{selectedIds.size} selected</Badge>
          <Button size="sm" variant="outline" onClick={handleBulkExtract}>
            <Play className="h-4 w-4 mr-2" />
            Run Extraction
          </Button>
          <Button size="sm" variant="destructive" onClick={handleBulkDelete}>
            Delete
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSelection}>
            <X className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Connected Sources</CardTitle>
          <CardDescription>
            Your configured data source connections
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">
              <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin opacity-20" />
              <p>Loading data sources...</p>
            </div>
          ) : (
            <DataSourcesTable
              dataSources={dataSources}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onTest={handleTest}
              onExtract={handleExtract}
              onViewHistory={handleViewHistory}
              onPreview={handlePreview}
            />
          )}
        </CardContent>
        {!isLoading && dataSources.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4 text-sm">
            <div className="text-muted-foreground">
              Showing {(currentPage - 1) * pageSize + 1}-
              {Math.min(currentPage * pageSize, pagination?.total ?? dataSources.length)} of{' '}
              {pagination?.total ?? dataSources.length}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Dialog */}
      <DataSourceForm
        open={isFormOpen}
        onClose={handleCloseForm}
        dataSource={selectedDataSource}
      />

      <DuplicateDataSourceDialog
        open={isDuplicateDialogOpen}
        sourceName={duplicateTarget?.name || 'data source'}
        duplicateName={duplicateName}
        isSubmitting={duplicateMutation.isPending}
        onDuplicateNameChange={setDuplicateName}
        onConfirm={handleConfirmDuplicate}
        onClose={closeDuplicateDialog}
      />

      {/* Extraction History Dialog */}
      <ExtractionHistory
        open={!!historyDataSource}
        onClose={() => setHistoryDataSource(null)}
        dataSource={historyDataSource}
      />

      {/* Extraction Preview Dialog */}
      <ExtractionPreviewDialog
        open={!!previewDataSource}
        onClose={() => setPreviewDataSource(null)}
        dataSource={previewDataSource}
      />
    </div>
  );
}
